import { COLUMN_IDS } from '../constants';

import type { ColumnId, Row } from '../constants';

import type { Item } from '@glideapps/glide-data-grid';

import { getRowValidationError } from './getRowValidationError';

type Validation = {
  populatedRows: [number, Row][];
  invalidRows: [number, Row][];
  error: string | null;
  errorRowRanges: number[][];
};

export const validateRows = (rows: Row[]): Validation => {
  const populatedRows: Validation['populatedRows'] = [];

  for (let i = 0; i < rows.length; i++) {
    const { ticker: tickerCell, ...otherCells } = rows[i];
    if (
      Object.values(otherCells).every((value) => value === null) &&
      Object.values(tickerCell).every((value) => value === null)
    ) {
      continue;
    }
    populatedRows.push([i, rows[i]]);
  }

  const invalidRows = populatedRows.filter(([_, row]) => {
    const { ticker: tickerCell, ...otherCells } = row;
    return (
      Object.values(tickerCell).some((value) => value === null) ||
      Object.values(otherCells).some((value) => value === null)
    );
  });

  if (invalidRows.length > 0) {
    const userReadableInvalidRowIndexes = invalidRows.map(
      ([rowIndex]) => rowIndex + 1
    );

    const { message = '', errorRowRanges = [] } =
      getRowValidationError(userReadableInvalidRowIndexes) ?? {};

    return { populatedRows, invalidRows, error: message, errorRowRanges };
  }

  return { populatedRows, invalidRows, error: null, errorRowRanges: [] };
};

const getInvalidItemsFromInvalidRow = (row: Row, rowIndex: number): Item[] => {
  const invalidItems: Item[] = [];
  const { currencyISO, ticker: tickerCell, ...otherCells } = row;
  if (Object.values(tickerCell).some((value) => value === null)) {
    invalidItems.push([COLUMN_IDS.indexOf('ticker'), rowIndex]);
  }
  return Object.keys(otherCells).reduce<Item[]>((items, column) => {
    if (otherCells[column] !== null) return items;
    return [...items, [COLUMN_IDS.indexOf(column as ColumnId), rowIndex]];
  }, invalidItems);
};

export const getInvalidItemsFromInvalidRows = (
  rows: Validation['invalidRows']
): Item[] =>
  rows.reduce<Item[]>((a, [rowIndex, row]) => {
    return [...a, ...getInvalidItemsFromInvalidRow(row, rowIndex)];
  }, []);
